import React, { useContext } from 'react'
import { func, bool, string } from 'prop-types'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import { STATE_FETCHING, STATE_DONE } from 'constants/stateTypes'
import { sceneItemsType, compositionDataItemsType } from 'models/propTypes'
import MobileMessage from 'components/MobileMessage'
import { click } from 'utils/tracker'

import Thumbnail from 'components/dashboard/Thumbnail'
import QuickAccessRow from 'components/dashboard/QuickAccessRow'

import SugarcubeContext from 'apps/SugarcubeContext'

require('./Dashboard.scss')

const Dashboard = ({
    sceneItems,
    stockSceneItems,
    compositionDataItems,
    stockCompositionDataItems,
    onSceneOpen,
    onCompositionOpen,
    onCompositionDelete,
    scenesLoading,
    isLoggedIn,
    earlyAccessStatus,
    onEarlyAccess,
    hasEmail,
}) => {
    const { t } = useTranslation()
    const { isMobile } = useContext(SugarcubeContext)

    let scenesHeader
    let scenesDescription
    if (!scenesLoading && !sceneItems.length) {
        scenesHeader = t('dashboard.scenesHeader_noScenes')
        scenesDescription = t('dashboard.scenesDescription_noScenes')
    } else if (!scenesLoading && sceneItems.length) {
        scenesHeader = t('dashboard.scenesHeader')
        scenesDescription = t('dashboard.scenesDescription')
    } else {
        scenesHeader = t('dashboard.loadingScenes')
        scenesDescription = ''
    }

    const earlyAccessButtonText =
        earlyAccessStatus === STATE_FETCHING ? t('dashboard.earlyAccessSigningUp') : t('dashboard.earlyAccessCTA')
    if (isMobile) {
        return (
            <div className="Dashboard">
                <div className="mobileMessage">
                    <MobileMessage />
                </div>
            </div>
        )
    }

    let sceneContent = null
    if (!scenesLoading) {
        if (!sceneItems.length) {
            sceneContent = [
                <div className="noScenesContainer" key="noScenes">
                    <div className="noScenes">
                        <div className="text">
                            {earlyAccessStatus === STATE_DONE ? (
                                <>
                                    <h2>{t('dashboard.thanks for your interest')}</h2>
                                    <p>{t('dashboard.early access thanks')}</p>
                                </>
                            ) : (
                                <>
                                    <h2>{t('dashboard.earlyAccessHeader')}</h2>
                                    <p>{t('dashboard.earlyAccessDescription')}</p>
                                    {hasEmail ? <button onClick={onEarlyAccess}>{earlyAccessButtonText}</button> : null}
                                </>
                            )}
                        </div>
                        <div className="image">
                            <img
                                src={`${ASSET_BUCKET}/graphics/dashboard/phone.png`}
                                alt={t('dashboard.alt.mobileApp')}
                            />
                        </div>
                    </div>
                </div>,
            ]
        } else {
            sceneContent = sceneItems.map((item) => (
                <Thumbnail
                    key={item.sceneId}
                    onClick={() => {
                        click('Dashboard.OpenRoom', {
                            RoomTitle: item.name,
                            RoomId: item.sceneId,
                            RoomSource: 'UserCaptured',
                        })
                        onSceneOpen(item.sceneId)
                    }}
                    item={item}
                    cta={t('dashboard.sceneDecorateCTA')}
                    data-testid="my-scenes"
                />
            ))
        }
    }
    return (
        <div className="Dashboard">
            {!isLoggedIn ? (
                <section>
                    <div className="sectionHeader">
                        <h3>{t('dashboard.scenesHeader_loggedOut')}</h3>
                    </div>
                    <p>
                        <Trans i18nKey="dashboard.scenesDescription_loggedOut">
                            <Link to="/signup">Sign up</Link> or <Link to="/login">log in</Link> for access
                        </Trans>
                    </p>
                </section>
            ) : (
                <QuickAccessRow
                    header={scenesHeader}
                    description={scenesDescription}
                    length={sceneItems && sceneItems.length}
                >
                    {sceneContent}
                </QuickAccessRow>
            )}
            {compositionDataItems.length ? (
                <QuickAccessRow
                    header={t('dashboard.compositionsHeader')}
                    description={t('dashboard.compositionsDescription')}
                    length={compositionDataItems && compositionDataItems.length}
                >
                    {compositionDataItems.map((item) => (
                        <Thumbnail
                            key={item.id}
                            onClick={() => {
                                click('Dashboard.OpenDesign', {
                                    DesignName: item.name,
                                    DesignId: item.id,
                                    RoomId: item.sceneId,
                                    DesignSource: 'UserCreated',
                                })
                                onCompositionOpen(item.id)
                            }}
                            item={item}
                            defaultName={t('design.default composition name')}
                            cta={t('dashboard.compositionOpenCTA')}
                            onDelete={() => {
                                click('Dashboard.DeleteDesign', {
                                    DesignName: item.name,
                                    DesignId: item.id,
                                    RoomId: item.sceneId,
                                })
                                onCompositionDelete(item.id)
                            }}
                            data-testid="my-compositions"
                        />
                    ))}
                </QuickAccessRow>
            ) : null}
            {stockCompositionDataItems.length ? (
                <QuickAccessRow
                    header={t('dashboard.stockCompHeader')}
                    description={t('dashboard.stockCompDescription')}
                    length={stockCompositionDataItems && stockCompositionDataItems.length}
                >
                    {stockCompositionDataItems.map((item) => (
                        <Thumbnail
                            key={item.id}
                            onClick={() => {
                                click('Dashboard.OpenDesign', {
                                    DesignName: item.name,
                                    DesignId: item.id,
                                    RoomId: item.sceneId,
                                    DesignSource: 'Stock',
                                })
                                onCompositionOpen(item.id)
                            }}
                            item={item}
                            cta={t('dashboard.compositionOpenCTA')}
                            data-testid="stock-compositions"
                        />
                    ))}
                </QuickAccessRow>
            ) : null}
            <QuickAccessRow
                header={t('dashboard.stockHeader')}
                description={t('dashboard.stockDescription')}
                length={stockSceneItems && stockSceneItems.length}
            >
                {stockSceneItems.map((item) => (
                    <Thumbnail
                        key={item.sceneId}
                        onClick={() => {
                            click('Dashboard.OpenRoom', {
                                RoomTitle: item.name,
                                RoomId: item.sceneId,
                                RoomSource: 'Stock',
                            })
                            onSceneOpen(item.sceneId)
                        }}
                        item={item}
                        cta={t('dashboard.sceneDecorateCTA')}
                        data-testid="stock-scenes"
                    />
                ))}
            </QuickAccessRow>
        </div>
    )
}

Dashboard.propTypes = {
    sceneItems: sceneItemsType.isRequired,
    stockSceneItems: sceneItemsType.isRequired,
    compositionDataItems: compositionDataItemsType.isRequired,
    stockCompositionDataItems: compositionDataItemsType.isRequired,
    onSceneOpen: func.isRequired,
    onCompositionOpen: func.isRequired,
    onCompositionDelete: func.isRequired,
    scenesLoading: bool.isRequired,
    isLoggedIn: bool.isRequired,
    earlyAccessStatus: string.isRequired,
    onEarlyAccess: func.isRequired,
    hasEmail: bool.isRequired,
}

export default Dashboard
